import { useNavigate } from 'react-router-dom';
import { ArrowLeftRight, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

interface AreaOption {
  id: string;
  label: string;
  icon: string;
  path: string;
  available: boolean;
}

const AREAS: AreaOption[] = [
  { id: 'funcionarios', label: 'Funcionarios', icon: '🏛️', path: '/app/chat', available: true },
  { id: 'generico', label: 'Base Común (General)', icon: '📚', path: '/app/base-comun', available: true },
  { id: 'secretaria', label: 'Secretaría-Intervención', icon: '⚖️', path: '/app/oposiciones', available: true },
  { id: 'c1', label: 'C1 Admin. Estado', icon: '🇪🇸', path: '/app/oposiciones-c1', available: true },
  { id: 'c2', label: 'C2 Auxiliar Estado', icon: '📋', path: '/app/oposiciones-c2', available: true },
  { id: 'guardia_civil', label: 'Guardia Civil', icon: '🛡️', path: '/app/guardia-civil', available: true },
  { id: 'policia_nacional', label: 'Policía Nacional', icon: '👮', path: '/app/policia-nacional', available: true },
  { id: 'iipp', label: 'Ayudante II.PP.', icon: '🔑', path: '/app/oposiciones-iipp', available: true },
];

interface AreaSwitcherProps {
  currentAreaId: string;
  /** Color del borde/acento (ej: 'violet' | 'blue') */
  accent?: 'violet' | 'blue' | 'neutral';
  /** IDs de áreas permitidas. Si no se pasan, se asume betatester (todas). */
  allowedIds?: string[];
  /** Título personalizado para el label superior */
  label?: string;
}

export function AreaSwitcher({ currentAreaId, accent = 'neutral', allowedIds, label }: AreaSwitcherProps) {
  const navigate = useNavigate();

  const borderClass =
    accent === 'violet'
      ? 'border-violet-200 hover:bg-violet-50'
      : accent === 'blue'
      ? 'border-blue-200 hover:bg-blue-50'
      : 'border-border hover:bg-muted';

  const current = AREAS.find((a) => a.id === currentAreaId);
  const visibleAreas = allowedIds 
    ? AREAS.filter(a => allowedIds.includes(a.id))
    : AREAS;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn('gap-1.5 text-xs shrink-0', borderClass)}
          title={label ?? "Cambiar de área"}
        >
          <ArrowLeftRight className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">{current?.label ?? 'Cambiar área'}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          {label ?? (allowedIds ? "Tus oposiciones activas" : "Modo betatester · ir a")}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {visibleAreas.map((area) => {
          const isCurrent = area.id === currentAreaId;
          return (
            <DropdownMenuItem
              key={area.id}
              disabled={!area.available || isCurrent}
              onClick={() => area.available && !isCurrent && navigate(area.path)}
              className={cn('gap-2', isCurrent && 'bg-muted/60 font-medium')}
            >
              <span className="text-base leading-none">{area.icon}</span>
              <span className="flex-1 truncate">{area.label}</span>
              {isCurrent ? (
                <Check className="h-3.5 w-3.5 text-primary" />
              ) : !area.available ? (
                <span className="text-[10px] text-muted-foreground uppercase tracking-wide">
                  Pronto
                </span>
              ) : null}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
